/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CustomAutoComplete } from 'commons/AutoComplete/CustomAutoComplete';
import { CustomButton } from 'commons/Button/CustomButton';
import { DialogType } from 'commons/Dialog/CustomDialog';
import { localize } from 'commons/LocalizedText/LocalizedText';
import { CustomTable } from 'commons/Table/CustomTable';
import { INITIAL_PAGE, INITIAL_ROWS_PER_PAGE } from 'commons/Table/CustomTableWithExternalPagination';
import {
  actionsHeader,
  creationDateHeader,
  creationUserHeader,
  descriptionHeader,
  documentNameHeader,
  documentTypeHeader
} from 'components/LaboAdmin/LaboAdminDocumentsHeaders';
import { UploadLaboDocumentDialog } from 'components/LaboAdmin/UploadLaboDocumentDialog';
import { useDialog } from 'hook/DialogProvider';
import useFetch from 'hook/UseFetch';
import useLocalizedSnackBar from 'hook/UseLocalizedSnackBar';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LaboratoryService } from 'services/LaboratoryService';

const useStyles = makeStyles((theme) => ({
  inputFields: {
    maxWidth: 500,
    width: '600px',
    marginLeft: '20px'
  },
  form: {
    marginBottom: '20px'
  }
}));


export const LaboAdminDocuments = () => {
  const classes = useStyles();
  const showDialog = useDialog();
  const { laboratory: laboratoryId } = useParams();
  const showSnackbar = useLocalizedSnackBar();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const typeOptions = useFetch(() => LaboratoryService.getLaboratoryDocumentTypes(), []).response;
  const [typeFilter, setTypeFilter] = useState([]);
  const [total, setTotal] = useState(null);

  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false);

  useEffect(() => {
    updateTable()
  }, [typeFilter]);

  const updateTable = () => {
    const queryParams = new URLSearchParams(window.location.search);

    const searchParams = {
      order: queryParams.get('order'),
      orderBy: queryParams.get('orderBy') || creationDateHeader.name,
      page: queryParams.get('page') || INITIAL_PAGE,
      rowsPerPage: queryParams.get('rowsPerPage') || INITIAL_ROWS_PER_PAGE
    }

    setIsLoading(true);
    LaboratoryService.getLaboratoryDocumentsByTypes(laboratoryId, typeFilter.map(typeEnum => typeEnum.id), searchParams)
      .then((resp) => {
        const files = resp.results;
        setRows(files);
        setTotal(resp?.total || 0);
        setIsLoading(false);
      })
      .catch((errorMessage) => {
        showDialog({
          variant: DialogType.ERROR,
          title: 'error',
          description: errorMessage
        });
        setIsLoading(false);
      })
  }

  const uploadLaboratoryDocument = (file, description) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", description);
    LaboratoryService.uploadLaboratoryDocument(laboratoryId, formData)
      .then(() => {
        showSnackbar('documentAdded', { variant: 'success' })
        updateTable();
      })
      .catch((errorMessage) => {
        showDialog({
          variant: DialogType.ERROR,
          title: 'error',
          description: errorMessage
        });
      })
      .finally(() => setIsLoading(false));
  }

  const onDelete = (row) => {
    showDialog({
      variant: DialogType.CONFIRMATION,
      title: 'confirmAction',
      description: localize('deleteReportConfirmation'),
      onSubmit: () => {
        setIsLoading(true);
        LaboratoryService.deleteLaboratoryDocument(laboratoryId, row.idCrypted)
          .then(() => {
            setRows((previousRows) => (previousRows.filter(prevRow => prevRow.idCrypted !== row.idCrypted)));
          })
          .catch((errorMessage) => {
            showDialog({
              variant: DialogType.ERROR,
              title: 'error',
              description: errorMessage
            });
          })
          .finally(() => setIsLoading(false));
      }
    });
  }

  return (
    <Grid container direction="column">
      <Grid item>
        <UploadLaboDocumentDialog
          fileName="laboratoryDocument"
          onClose={() => setUploadDialogOpen(false)}
          onSubmit={uploadLaboratoryDocument}
          open={isUploadDialogOpen}
        />
        <Box className={classes.form} style={{overflow: 'hidden'}}>
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="space-between"
            wrap='nowrap'
          >
            <Grid item>
              <CustomAutoComplete
                className={classes.inputFields}
                defaultValue={typeOptions}
                getOptionLabel={(option) => localize(option.id)}
                isMultiple
                label="type"
                limitTags={-1}
                onChange={(event, value) => setTypeFilter(value)}
                options={typeOptions}
                valueName="selectedDocumentTypes"
              />
            </Grid>
            <Grid item>
              <CustomButton
                onClick={() => setUploadDialogOpen(true)}
              >
                {localize('addDocumentButton')}
              </CustomButton>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid item>
        <CustomTable
          defaultOrder="desc"
          headers={[
            documentNameHeader,
            documentTypeHeader,
            creationDateHeader,
            creationUserHeader,
            descriptionHeader,
            actionsHeader(onDelete)
          ]}
          isBackend
          isLoading={isLoading}
          onChange={updateTable}
          rowKey="name"
          rows={rows}
          rowsPerPage={INITIAL_ROWS_PER_PAGE}
          stickyHeader
          total={total}
        />
      </Grid>
    </Grid>
  );
}